import type React from "react";
import { memo, useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import Icon, { ICONS } from "@/components/Core/Icons";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import {
  getAxiosErorrMessage,
  getUnexpiredToken,
  useUserId,
} from "@/utils/utils";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { useSession } from "@clerk/clerk-react";

type PromoStatus = "loading" | "success" | "error" | "invalid-code";

interface PromoResult {
  success: boolean;
  message: string;
  package_id?: string;
  num_prompts?: number;
}

interface PromoCodeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  promoCode?: string;
}

const PromoCodeModal: React.FC<PromoCodeModalProps> = ({
  open,
  onOpenChange,
  promoCode,
}) => {
  const [status, setStatus] = useState<PromoStatus>("loading");
  const [result, setResult] = useState<PromoResult | null>(null);
  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
  const auth0UserId = useUserId();
  const { session } = useSession();

  useEffect(() => {
    // Reset state when modal opens with a new promo code
    if (open && promoCode) {
      setStatus("loading");
      setResult(null);
      redeemPromoCode();
    }
  }, [open, promoCode]);

  const redeemPromoCode = async () => {
    if (!promoCode) {
      setStatus("invalid-code");
      setResult({ success: false, message: "No promo code provided" });
      return;
    }

    if (!supabaseClient) {
      setStatus("error");
      setResult({
        success: false,
        message: "System not ready. Please try again.",
      });
      return;
    }

    if (!auth0UserId) {
      setStatus("error");
      setResult({ success: false, message: "User not authenticated" });
      return;
    }

    try {
      const { data: result } = await axios.post<PromoResult>(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/promo/redeem`,
        {
          promo_code: promoCode,
        },
        {
          headers: {
            Authorization: `Bearer ${await getUnexpiredToken(session)}`,
          },
        }
      );

      setResult(result);

      if (result.success) {
        setStatus("success");
      } else {
        setStatus("error");
      }
    } catch (err) {
      console.error("Error redeeming promo code:", err);
      setStatus("error");
      setResult({ success: false, message: getAxiosErorrMessage(err) });
    }
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  const getContentConfig = () => {
    switch (status) {
      case "loading":
        return {
          title: "Redeeming your promo code...",
          message: "Please wait while we process your promo code.",
        };
      case "success":
        return {
          title: `You've got ${result?.num_prompts} free AI prompts!`,
          message:
            (promoCode === "shipaton"
              ? "We’re excited you’re participating in the Shipaton hosted by RevenueCat! Your 150 free prompts are ready to use."
              : result?.message) ||
            `Your ${result?.num_prompts} free prompts are ready to use!`,
        };
      case "invalid-code":
        return {
          title: "Invalid promo code",
          message:
            "No promo code was provided. Please check your link and try again.",
        };
      case "error":
        return {
          title: "Failed to redeem promo code",
          message:
            result?.message ||
            "An error occurred while redeeming your promo code. Please try again later.",
        };
    }
  };

  const renderContent = () => {
    const config = getContentConfig();

    return (
      <div className="flex flex-col items-center py-8">
        {/* Logo Section - Always the same */}
        <div className="flex items-center gap-2 mb-4">
          <div className="bg-background-secondary p-3 rounded-lg">
            <Icon icon={ICONS.TEMPO_LOGO_WHITE} className="w-8 h-8" />
          </div>
          <Icon icon={ICONS.DIVIDER} className="w-8 h-8" />
          <div className="p-3 rounded-lg bg-background-secondary">
            <Icon icon={ICONS.AI_LOGO} className="w-8 h-8" />
          </div>
        </div>

        {/* Dynamic Content */}
        <h2 className="text-lg font-bold mb-4">{config.title}</h2>

        <p className="text-sm text-text-secondary text-center mb-6">
          {config.message}
        </p>

        {/* Button Section - Always the same */}
        <Button
          onClick={handleClose}
          className="h-7 px-2 py-3 bg-primary-0 text-text-primary rounded-md hover:bg-primary-100 transition-colors"
          size="xs"
        >
          Start building now
        </Button>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md border border-border-primary">
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default memo(PromoCodeModal);
